import React, {useState, useMemo, useEffect} from 'react';
import {
	Modal,
	FlexRow,
	Button,
	DateRangePicker,
	Text,
	TextField,
	Icon,
	FlexColumn,
	DropdownList,
	Frame,
	DraggableContainer,
	Tooltip,
} from '@forecast-it/design-system';
import {FormattedMessage, useIntl} from 'react-intl';
import styled from 'styled-components';
import {showModal, MODAL_TYPE} from '../../forecast-app/shared/components/modals/generic_modal_conductor';
import Util from '../../forecast-app/shared/util/util';
import {dateObjectToIsoDate, dateFragmentPropsFromIntervalInIsoFormat} from '../../forecast-app/shared/util/DateUtil';
import {DragDropContext, Draggable, Droppable, DropResult} from 'react-beautiful-dnd';
import {DatePickerOnChange} from '../../components/expense-management/PredefinedDateRangePicker';
import {sortByStartDate} from '../../forecast-app/project-tab/projects/project-retainer-periods/RetainerPeriodUtil';
import BulkUpdatePhasesMutation from '../../mutations/bulk_update_phases_mutation';
import DeletePhaseMutation from '../../mutations/delete_phase_mutation';
import BulkUpdatePhasePortalConfigMutation from '../../mutations/bulk_update_phase_portal_config_mutation';
import {isCustomerPortalAvailable} from '../../forecast-app/shared/util/customerPortalAvailability';
import {Checkbox} from '@forecast-it/design-system';
import {PhaseModalPhaseNode, Project, BaselineItem} from '../../components/baseline/BaselineTypes';
import {resolvePhaseDateRange} from '../../components/baseline/PhaseDateResolution';
import type {bulkUpdatePhasesMutation} from '@/mutations/__generated__/bulkUpdatePhasesMutation.graphql';
import PortalApproverPicker from '../../forecast-app/shared/components/pickers/PortalApproverPicker';

type BulkUpdatePhasesMutationResponse = bulkUpdatePhasesMutation['response'];

const PhaseColumnHeader = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	width: 100%;
	gap: 27rem;
`;

export type Phase = {
	node: PhaseModalPhaseNode;
};

const ORDER_OPTIONS = {
	CUSTOM: 'custom',
	ASCENDING: 'start_date_ascending',
	DESCENDING: 'start_date_descending',
};

/**
 * A phase's client-workspace sign-off config, as edited in this modal.
 *
 * `signoffApproverForecastPersonId` is a raw Forecast person id (the workspace holds
 * a soft int link, not a Relay global id). `signoffLocked` is read-only here: it is
 * set once a client has signed the phase off, and the workspace refuses further
 * writes to that phase.
 */
type PhaseSignoffState = {
	requiresClientSignoff: boolean;
	signoffApproverForecastPersonId: number | null;
	signoffLocked: boolean;
};

type PhaseManagementModalProps = {
	closeModal: () => void;
	initialPhases: Phase[];
	project: Project;
	baselineItems: BaselineItem[];
	onSave?: (result: BulkUpdatePhasesMutationResponse) => void;
	editBaselineDates?: boolean;
	/**
	 * The project-level sign-off approver, used as the default for any phase that has
	 * no approver of its own. Overridable per phase.
	 */
	projectDefaultApproverId?: number | null;
};

export const buildBaselineSavePayload = (phase: Phase, index: number, projectId: string, editBaselineDates: boolean) => {
	const base = {
		id: phase.node.id.startsWith('new-phase') ? undefined : phase.node.id,
		projectId,
		name: phase.node.name,
		sortOrder: index + 1,
	};
	if (editBaselineDates) {
		return {
			...base,
			baselineStartYear: phase.node.baselineStartYear,
			baselineStartMonth: phase.node.baselineStartMonth,
			baselineStartDay: phase.node.baselineStartDay,
			baselineDeadlineYear: phase.node.baselineDeadlineYear,
			baselineDeadlineMonth: phase.node.baselineDeadlineMonth,
			baselineDeadlineDay: phase.node.baselineDeadlineDay,
		};
	}
	return {
		...base,
		startYear: phase.node.startYear,
		startMonth: phase.node.startMonth,
		startDay: phase.node.startDay,
		deadlineYear: phase.node.deadlineYear,
		deadlineMonth: phase.node.deadlineMonth,
		deadlineDay: phase.node.deadlineDay,
	};
};

const PhaseManagementModal = ({
	closeModal,
	initialPhases,
	project,
	baselineItems,
	onSave,
	editBaselineDates = false,
	projectDefaultApproverId = null,
}: PhaseManagementModalProps): React.JSX.Element => {
	const {formatMessage} = useIntl();
	const [changesMade, setChangesMade] = useState(false);
	const projectId = project.id;

	const phasesSortedBySortOrder = initialPhases
		.filter(phase => phase && phase.node && phase.node.sortOrder !== undefined)
		.sort((a, b) => a.node.sortOrder - b.node.sortOrder);
	const [phases, setPhases] = useState<Phase[]>(phasesSortedBySortOrder);
	const [newPhasesCount, setNewPhasesCount] = useState<number>(0);
	const numberOfPhases = phases.length;
	const findOrderSetting = () => {
		const phaseIds = phases.map(phase => phase.node.id);
		const sortedPhaseIds = [...phases].sort((a, b) => sortByStartDate(a.node, b.node)).map(phase => phase.node.id);
		if (phaseIds.every((id, index) => id === sortedPhaseIds[index])) {
			return ORDER_OPTIONS.ASCENDING;
		}
		if (phaseIds.every((id, index) => id === sortedPhaseIds[sortedPhaseIds.length - 1 - index])) {
			return ORDER_OPTIONS.DESCENDING;
		}
		return ORDER_OPTIONS.CUSTOM;
	};
	const [phaseOrder, setPhaseOrder] = useState(findOrderSetting);
	// Per-phase "Requires client sign-off" + approver config. Kept in a local map keyed
	// by phase id so it never rides the forecast_db BulkUpdatePhases write; it is
	// persisted separately to the client workspace.
	const showPortalSignoff = isCustomerPortalAvailable();
	// Baseline hydrated from each phase's persisted workspace config. We diff against it
	// on save so untouched phases are NEVER re-written — otherwise editing one phase
	// would clobber every other phase's saved values.
	const signoffBaseline = useMemo(() => {
		const map: Record<string, PhaseSignoffState> = {};
		for (const phase of phases) {
			const cfg = phase.node.portalConfig;
			map[phase.node.id] = {
				requiresClientSignoff: !!cfg?.requiresClientSignoff,
				signoffApproverForecastPersonId: cfg?.signoffApproverForecastPersonId ?? null,
				signoffLocked: !!cfg?.signoffLocked,
			};
		}
		return map;
	}, [phases]);
	const [signoffByPhaseId, setSignoffByPhaseId] = useState<Record<string, PhaseSignoffState>>(signoffBaseline);
	// Re-hydrate if the phase set / persisted config changes (e.g. a relay refetch).
	useEffect(() => {
		setSignoffByPhaseId(signoffBaseline);
	}, [signoffBaseline]);
	const emptySignoffState: PhaseSignoffState = {
		requiresClientSignoff: false,
		signoffApproverForecastPersonId: null,
		signoffLocked: false,
	};
	const getSignoffState = (phaseId: string): PhaseSignoffState =>
		signoffByPhaseId[phaseId] ?? signoffBaseline[phaseId] ?? emptySignoffState;
	const updateSignoffState = (phaseId: string, change: Partial<PhaseSignoffState>) => {
		setSignoffByPhaseId(prev => {
			const current = prev[phaseId] ?? signoffBaseline[phaseId] ?? emptySignoffState;
			return {...prev, [phaseId]: {...current, ...change}};
		});
	};
	const togglePhaseSignoff = (phaseId: string) => {
		updateSignoffState(phaseId, {requiresClientSignoff: !getSignoffState(phaseId).requiresClientSignoff});
	};
	const setPhaseApprover = (phaseId: string, personId: number | null) => {
		updateSignoffState(phaseId, {signoffApproverForecastPersonId: personId});
	};
	/**
	 * The approver shown for a phase: its own if it has one, otherwise the project
	 * default. Displaying the project default is not the same as having saved it — the
	 * save diff below compares the phase's *own* value, so merely opening the modal
	 * never persists an inherited default.
	 */
	const getDisplayedApprover = (phaseId: string): number | null =>
		getSignoffState(phaseId).signoffApproverForecastPersonId ?? projectDefaultApproverId ?? null;
	const dragAndDropGroup = 'Phases Group';
	const orderedPhaseIds = useMemo(() => {
		return phases.map(phase => phase.node.id);
	}, [phases]);

	const handlePhaseManagement = () => {
		closeModal();
		// Persist per-phase sign-off config to the client workspace (separate from the
		// forecast_db phase write). The graphql proxy decodes each phase's global id to
		// its backend id. Only CHANGED phases are sent, so an untouched phase's saved
		// values are never clobbered.
		if (showPortalSignoff) {
			const portalPhases = phases
				.filter(phase => !phase.node.id.startsWith('new-phase'))
				// A signed-off phase is locked: the workspace refuses writes to it, so it
				// is never sent even if something managed to change its local state.
				.filter(phase => !getSignoffState(phase.node.id).signoffLocked)
				.filter(phase => {
					const current = getSignoffState(phase.node.id);
					const base = signoffBaseline[phase.node.id] ?? emptySignoffState;
					return (
						current.requiresClientSignoff !== base.requiresClientSignoff ||
						current.signoffApproverForecastPersonId !== base.signoffApproverForecastPersonId
					);
				})
				.map(phase => {
					const current = getSignoffState(phase.node.id);
					return {
						psaMilestoneId: String(phase.node.id),
						requiresClientSignoff: current.requiresClientSignoff,
						signoffApproverForecastPersonId: current.signoffApproverForecastPersonId,
					};
				});
			if (portalPhases.length > 0) {
				Util.CommitMutation(BulkUpdatePhasePortalConfigMutation, {phases: portalPhases});
			}
		}
		if (changesMade) {
			if (phases.length === 0) {
				for (const initialPhase of initialPhases) {
					Util.CommitMutation(DeletePhaseMutation, {
						id: initialPhase.node.id,
						projectId: projectId,
					});
				}
			} else {
				const mutationObject = {
					phases: phases.map((phase, index) => buildBaselineSavePayload(phase, index, projectId, editBaselineDates)),
				};
				Util.CommitMutation(BulkUpdatePhasesMutation, mutationObject, onSave);
			}
		}
	};

	const moveToPosition = (sourceIndex: number, destinationIndex: number) => {
		const updatedOrderedPhaseIds = Array.from(orderedPhaseIds);
		const [movedPhaseId] = updatedOrderedPhaseIds.splice(sourceIndex, 1);
		updatedOrderedPhaseIds.splice(destinationIndex, 0, movedPhaseId);
		const updatedPhases = updatedOrderedPhaseIds.map(id => {
			const phase = phases.find(phase => phase.node.id === id);
			if (!phase) {
				throw new Error(`Phase with id ${id} not found`);
			}
			return phase;
		});
		setPhases(updatedPhases);
		setChangesMade(true);
		setPhaseOrder('custom');
	};

	const onPhaseOrderChange = (newOrder: string) => {
		setPhaseOrder(newOrder);
		setChangesMade(true);
		const updatedPhases = [...phases];
		switch (newOrder) {
			case ORDER_OPTIONS.ASCENDING:
				updatedPhases.sort((a, b) => sortByStartDate(a.node, b.node));
				break;
			case ORDER_OPTIONS.DESCENDING:
				updatedPhases.sort((a, b) => sortByStartDate(b.node, a.node));
				break;
			default:
				return;
		}
		setPhases(updatedPhases);
		setChangesMade(true);
	};

	const handleOnDragEnd = (result: DropResult) => {
		if (!result.destination) return;
		const {source, destination} = result;
		moveToPosition(source.index, destination.index);
	};

	const setPhaseName = (name: string, index: number) => {
		const updatedPhases = [...phases];
		if (updatedPhases[index]) {
			updatedPhases[index] = {
				...updatedPhases[index],
				node: {
					...updatedPhases[index].node,
					name: name,
				},
			};
		}
		setPhases(updatedPhases);
		setChangesMade(true);
	};

	const handleDateRangeChange = (date: DatePickerOnChange, index: number) => {
		if (!date.startDate || !date.endDate) {
			return;
		}
		const updatedDateRange = dateFragmentPropsFromIntervalInIsoFormat(date.startDate, date.endDate);
		const updatedPhases = [...phases];
		if (updatedPhases[index]) {
			const dateFields = editBaselineDates
				? {
						baselineStartDay: updatedDateRange.startDay,
						baselineStartMonth: updatedDateRange.startMonth,
						baselineStartYear: updatedDateRange.startYear,
						baselineDeadlineDay: updatedDateRange.endDay,
						baselineDeadlineMonth: updatedDateRange.endMonth,
						baselineDeadlineYear: updatedDateRange.endYear,
				  }
				: {
						startDay: updatedDateRange.startDay,
						startMonth: updatedDateRange.startMonth,
						startYear: updatedDateRange.startYear,
						deadlineDay: updatedDateRange.endDay,
						deadlineMonth: updatedDateRange.endMonth,
						deadlineYear: updatedDateRange.endYear,
				  };
			updatedPhases[index] = {...updatedPhases[index], node: {...updatedPhases[index].node, ...dateFields}};
		}
		setPhases(updatedPhases);
		setChangesMade(true);
	};

	const addNewPhase = () => {
		const newIndex = newPhasesCount + 1;
		setNewPhasesCount(newIndex);
		const newPhase = {
			node: {
				id: 'new-phase' + newIndex,
				projectId: projectId,
				startYear: project.projectStartYear,
				startDay: project.projectStartDay,
				startMonth: project.projectStartMonth,
				deadlineYear: project.projectEndYear,
				deadlineDay: project.projectEndDay,
				deadlineMonth: project.projectEndMonth,
				name: 'New Phase ' + newIndex,
				sortOrder: phases.length,
			},
		};
		setPhases(prevPhases => [...prevPhases, newPhase]);
		setChangesMade(true);
	};

	const phasesWithEstimates = useMemo(
		() => new Set(baselineItems ? baselineItems.map(item => item.phaseId) : []),
		[baselineItems]
	);

	const onDeleteSuccess = (index: number) => {
		const updatedPhases = [...phases];
		updatedPhases.splice(index, 1);
		setPhases(updatedPhases);
		setChangesMade(true);
	};

	const onDeletePhase = (index: number, phase: Phase) => {
		showModal({
			type: MODAL_TYPE.DELETE_PHASE_MODAL,
			callback: () => {
				onDeleteSuccess(index);
			},
			hasEstimates: phasesWithEstimates.has(phase.node.id),
		});
	};

	const getContextMenuOptions = (phase: Phase, index: number) => {
		const disableTop = index === 0;
		const disableBottom = index === phases.length - 1;
		const contextMenuOptions = [
			{
				name: 'Move to the top',
				onClick: () => {
					moveToPosition(index, 0);
				},
				disabled: disableTop,
			},
			{
				name: 'Move up',
				onClick: () => {
					moveToPosition(index, index - 1);
				},
				disabled: disableTop,
			},
			{
				name: 'Move down',
				onClick: () => {
					moveToPosition(index, index + 1);
				},
				disabled: disableBottom,
			},
			{
				name: 'Move to the bottom',
				onClick: () => {
					moveToPosition(index, phases.length - 1);
				},
				disabled: disableBottom,
			},
			{
				name: 'Delete Phase',
				onClick: () => {
					onDeletePhase(index, phase);
				},
			},
		];
		return contextMenuOptions;
	};

	// Resolved as a whole range, not per field: the date picker writes both endpoints back through
	// handleDateRangeChange, so a per-field fallback would let a user persist a Scoping deadline into
	// baseline_deadline_* just by nudging the start date. See PhaseDateResolution.
	const getPhaseRange = (n: PhaseModalPhaseNode) =>
		resolvePhaseDateRange(
			editBaselineDates,
			{
				start: {year: n.baselineStartYear, month: n.baselineStartMonth, day: n.baselineStartDay},
				end: {year: n.baselineDeadlineYear, month: n.baselineDeadlineMonth, day: n.baselineDeadlineDay},
			},
			{
				start: {year: n.startYear, month: n.startMonth, day: n.startDay},
				end: {year: n.deadlineYear, month: n.deadlineMonth, day: n.deadlineDay},
			}
		);

	const getStart = (n: PhaseModalPhaseNode) => {
		const {start} = getPhaseRange(n);
		return {y: start.year, m: start.month, d: start.day};
	};

	const getDeadline = (n: PhaseModalPhaseNode) => {
		const {end} = getPhaseRange(n);
		return {y: end.year, m: end.month, d: end.day};
	};

	return (
		<Modal size={Modal.SIZE.EXTRA_LARGE} onCloseModal={closeModal} setHeight>
			<Modal.Title>
				<FormattedMessage id={'phase_management.manage_phases'} />
			</Modal.Title>
			<Modal.Content>
				<FlexColumn gap={'l'}>
					<FlexRow gap={'m'}>
						<Button emphasis={'medium'} onPress={addNewPhase} data-cy={'add-new-phase'}>
							<FormattedMessage id={'phase_management.new_phase'} />
						</Button>
						<DropdownList>
							<DropdownList.Trigger>
								<Button emphasis={'medium'} rightIcon={'chevronDown'} disabled={phases.length === 0}>
									<FormattedMessage id={'phase_management.order'} />
									<FormattedMessage id={`phase_management.${phaseOrder}`} />
								</Button>
							</DropdownList.Trigger>
							<DropdownList.Content align={'start'}>
								{Object.values(ORDER_OPTIONS).map(option => (
									<DropdownList.Item onClick={() => onPhaseOrderChange(option)}>
										<FormattedMessage id={'phase_management.order'} />
										<FormattedMessage id={`phase_management.${option}`} />
									</DropdownList.Item>
								))}
							</DropdownList.Content>
						</DropdownList>
					</FlexRow>
					<FlexRow>
						<Icon icon={'phase'} />
						<Text size={'2'}>
							{formatMessage({id: 'phase_management.phases'}, {numberOfPhases: numberOfPhases})}
						</Text>
					</FlexRow>
					<PhaseColumnHeader>
						<Text size={'1'}>
							<FormattedMessage id={'phase_management.phase_name'} />
						</Text>
						<FlexRow gap={'xs'}>
							<Text size={'1'}>
								<FormattedMessage
									id={
										editBaselineDates
											? 'phase_management.baseline_phase_dates'
											: 'phase_management.phase_dates'
									}
								/>
							</Text>
							{editBaselineDates && (
								<Tooltip
									interactable={false}
									content={formatMessage({id: 'phase_management.baseline_phase_dates_tooltip'})}
								>
									<Icon icon={'info'} />
								</Tooltip>
							)}
						</FlexRow>
					</PhaseColumnHeader>
					{phases.length !== 0 ? (
						<DragDropContext
							scrollYSpeed={25}
							onDragEnd={handleOnDragEnd}
							outerScrollBar={true}
							dragAndDropGroup={dragAndDropGroup}
						>
							<Droppable
								dragAndDropGroup={dragAndDropGroup}
								droppableId="phasesDroppable"
								dynamicElemHeight={true}
								minElemHeight={40}
							>
								{provided => (
									<div {...provided.droppableProps} ref={provided.innerRef}>
										<FlexColumn gap={'s'}>
											{phases.map((phase, index) => {
												const {y: startY, m: startM, d: startD} = getStart(phase.node);
												const {y: deadlineY, m: deadlineM, d: deadlineD} = getDeadline(phase.node);
												const signoff = getSignoffState(phase.node.id);
												const isLocked = signoff.signoffLocked;
												return (
													<Draggable
														key={phase.node.id}
														draggableId={phase.node.id}
														index={index}
														dragAndDropGroup={dragAndDropGroup}
														noCancelOnMove={true}
													>
														{provided => (
															<div
																ref={provided.innerRef}
																{...provided.draggableProps}
																{...provided.dragHandleProps}
															>
																<DraggableContainer>
																	<TextField
																		data-cy={'new-project-name'}
																		value={phase.node.name || ''}
																		onValueChange={value => setPhaseName(value, index)}
																	/>
																	<DateRangePicker
																		startDate={dateObjectToIsoDate(
																			Util.CreateNonUtcMomentDate(startY, startM, startD)
																		)}
																		endDate={dateObjectToIsoDate(
																			Util.CreateNonUtcMomentDate(
																				deadlineY,
																				deadlineM,
																				deadlineD
																			)
																		)}
																		onChange={(date: DatePickerOnChange) =>
																			handleDateRangeChange(date, index)
																		}
																		aria-label="Select date range"
																		data-cy={'date-range-picker'}
																		disabled={false}
																	/>
																	{showPortalSignoff &&
																		!phase.node.id.startsWith('new-phase') && (
																			<>
																				<Checkbox
																					checked={signoff.requiresClientSignoff}
																					onChange={() =>
																						togglePhaseSignoff(phase.node.id)
																					}
																					disabled={isLocked}
																					data-cy={'phase-signoff-toggle'}
																					aria-label={formatMessage({
																						id: 'client_portal.requires_signoff',
																						defaultMessage:
																							'Requires client sign-off',
																					})}
																				/>
																				{isLocked && (
																					<Text size={'1'}>
																						{formatMessage({
																							id: 'client_portal.signoff_locked_label',
																							defaultMessage:
																								'Signed off — locked',
																						})}
																					</Text>
																				)}
																				<PortalApproverPicker
																					value={getDisplayedApprover(phase.node.id)}
																					disabled={isLocked}
																					onChange={personId =>
																						setPhaseApprover(
																							phase.node.id,
																							personId
																						)
																					}
																					ariaLabel={formatMessage({
																						id: 'client_portal.signoff_approver_label',
																						defaultMessage: 'Sign-off approver',
																					})}
																					placeholder={formatMessage({
																						id: 'client_portal.signoff_approver_placeholder',
																						defaultMessage: 'Project default',
																					})}
																					dataCy={'phase-signoff-approver'}
																				/>
																			</>
																		)}
																	<DraggableContainer.ContextMenu
																		contextMenuOptions={getContextMenuOptions(phase, index)}
																	/>
																</DraggableContainer>
															</div>
														)}
													</Draggable>
												);
											})}
											{provided.placeholder}
										</FlexColumn>
									</div>
								)}
							</Droppable>
						</DragDropContext>
					) : (
						<Frame height={'s'} centerContent={true}>
							<FlexRow justifyContent={'center'}>
								<FormattedMessage id={'phase_management.no_phases'} />
							</FlexRow>
						</Frame>
					)}
				</FlexColumn>
			</Modal.Content>
			<Modal.Footer>
				<Modal.PrimaryFooterButton onPress={handlePhaseManagement} dataCy={'phase-management-save'}>
					<FormattedMessage id="common.save_changes" />
				</Modal.PrimaryFooterButton>
				<Modal.SecondaryFooterButton onPress={closeModal}>
					<FormattedMessage id="common.cancel" />
				</Modal.SecondaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
};

export default PhaseManagementModal;
